import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Lock, Bell, CalendarCheck, ShieldCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLenis } from "../hooks/useLenis";
import { useAuth } from "../context/AuthContext";

const HERO_IMG =
  "https://images.pexels.com/photos/9099794/pexels-photo-9099794.jpeg";
const RANGE_IMG =
  "https://images.pexels.com/photos/17314914/pexels-photo-17314914.jpeg";
const FIELD_IMG =
  "https://images.unsplash.com/photo-1759167625071-069dc252702f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHx0YWN0aWNhbCUyMGdlYXIlMjBkYXJrfGVufDB8fHx8MTc4NzA4MDYyNHww&ixlib=rb-4.1.0&q=85";

const HERO_LINES = ["CLAIM", "YOUR", "EVENT."];

const CHAPTERS = [
  {
    n: "01",
    title: "See Every Event Ahead",
    body: "Workshops, concealed carry classes, and table events — the entire association schedule, laid out in one raw grid. No guesswork, no group chats.",
    icon: CalendarCheck,
  },
  {
    n: "02",
    title: "Book It Yourself",
    body: "Find an open event and claim it in one tap. The moment you book, the event locks — no other rep can grab it out from under you.",
    icon: Lock,
  },
  {
    n: "03",
    title: "Never Miss A Beat",
    body: "Text reminders fire two days out, one day out, and the morning of. Show up sharp, represent Right To Bear, and pitch with confidence.",
    icon: Bell,
  },
  {
    n: "04",
    title: "Show Up & Protect",
    body: "Every event you work puts another person on the path to being armed, trained, and covered. Show up and help people get protected with Right To Bear.",
    icon: ShieldCheck,
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const heroRef = useRef(null);
  useLenis(true);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  const go = () => navigate(user ? "/dashboard" : "/register");

  return (
    <div className="bg-[#1A1A1A] text-white">
      <Navbar />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="grid lg:grid-cols-[1.15fr_1fr] min-h-[92vh]">
          {/* Left kinetic type */}
          <div className="relative z-10 flex flex-col justify-center px-5 sm:px-10 lg:px-16 py-20 grid-lines">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="overline text-[hsl(var(--primary))] mb-6"
            >
              Right To Bear · Rep Command Center For Region 4
            </motion.div>

            <h1 className="font-display leading-[0.82] tracking-tight text-[19vw] sm:text-[14vw] lg:text-[10rem]">
              {HERO_LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 0.8, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {i === 2 ? (
                      <span>
                        {line.slice(0, -1)}
                        <span className="text-[hsl(var(--primary))]">.</span>
                      </span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-8 max-w-md text-white/60 leading-relaxed text-base"
            >
              The scheduling command center for Right To Bear representatives for Region 4. See every
              workshop, class, and table event — and book your event you want to pitch before anyone else does.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <button
                data-testid="hero-cta"
                onClick={go}
                className="group flex items-center gap-2 bg-[hsl(var(--primary))] text-white overline px-7 py-4 hard-shadow-hover"
              >
                {user ? "Go To Schedule" : "Join The Team"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              {!user && (
                <button
                  data-testid="hero-login"
                  onClick={() => navigate("/login")}
                  className="border border-white/20 text-white overline px-7 py-4 hover:border-white/60 transition-colors"
                >
                  Rep Log In
                </button>
              )}
            </motion.div>
          </div>

          {/* Right parallax image */}
          <div className="relative overflow-hidden border-l border-white/10 min-h-[40vh] lg:min-h-full">
            <motion.img
              src={HERO_IMG}
              alt="Tactical gear"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <motion.div
              style={{ opacity: overlayOpacity }}
              className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-[#1A1A1A]/40"
            />
            <div className="absolute bottom-0 left-0 right-0 p-8 flex items-end justify-between">
              <div className="overline text-white/70">Represent · Protect · Pitch</div>
              <div className="font-display text-6xl stroke-text leading-none">2A</div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-white/10 py-6 bg-[#1A1A1A]">
        <Marquee speed={40} gradient={false} autoFill>
          {["WORKSHOPS", "CONCEALED CARRY CLASSES", "TABLE EVENTS", "BOOK YOUR EVENT"].map(
            (t, i) => (
              <span
                key={i}
                className="font-display text-6xl sm:text-8xl stroke-text mx-8 leading-none"
              >
                {t} <span className="text-[hsl(var(--primary))] not-italic">/</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline text-[hsl(var(--primary))] mb-4">The Doctrine</div>
          <h2 className="font-display text-5xl sm:text-6xl leading-[0.9] tracking-tight">
            ONE SCHEDULE.
            <br />
            EVERY REP.
            <br />
            <span className="text-[hsl(var(--primary))]">ZERO OVERLAP.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {CHAPTERS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                className="relative bg-[#1A1A1A] p-8 lg:p-12 group"
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="font-display text-7xl lg:text-8xl stroke-text leading-none group-hover:text-[hsl(var(--primary))] group-hover:[-webkit-text-stroke:0] transition-colors">
                    {c.n}
                  </span>
                  <Icon className="w-7 h-7 text-[hsl(var(--primary))]" strokeWidth={2} />
                </div>
                <h3 className="font-heading font-extrabold text-2xl mb-3">{c.title}</h3>
                <p className="text-white/55 leading-relaxed">{c.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* SPLIT IMAGE BLOCK */}
      <section className="grid lg:grid-cols-2 border-y border-white/10">
        <div className="relative min-h-[50vh] overflow-hidden">
          <img src={RANGE_IMG} alt="Range" className="w-full h-full object-cover absolute inset-0" />
          <div className="absolute inset-0 bg-[#1A1A1A]/40" />
        </div>
        <div className="bg-[#22303C] flex flex-col justify-center p-10 lg:p-20">
          <div className="overline text-[hsl(var(--primary))] mb-5">For Representatives</div>
          <h2 className="font-display text-4xl sm:text-5xl leading-[0.92] tracking-tight mb-6">
            SHOW UP.
            <br />
            STAND OUT.
            <br />
            SIGN THEM UP.
          </h2>
          <p className="text-white/55 leading-relaxed max-w-md mb-8">
            Every event is a chance to grow the association. Grab the events that fit your
            calendar, get your reminders, and represent Right To Bear where it matters most.
          </p>
          <button
            data-testid="split-cta"
            onClick={go}
            className="self-start flex items-center gap-2 bg-[hsl(var(--primary))] text-white overline px-7 py-4 hard-shadow-hover"
          >
            Browse The Schedule <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden py-28 lg:py-40 text-center">
        <img src={FIELD_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#1A1A1A]/70" />
        <div className="relative z-10 max-w-3xl mx-auto px-5">
          <h2 className="font-display text-6xl sm:text-8xl leading-[0.85] tracking-tight mb-8">
            BOOK REGION 4 EVENTS
            <br />
            <span className="text-[hsl(var(--primary))]">STARTS HERE.</span>
          </h2>
          <button
            data-testid="final-cta"
            onClick={go}
            className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white overline px-10 py-5 hard-shadow-hover text-sm"
          >
            {user ? "Open Dashboard" : "Create Your Rep Account"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
